export const isHardMode = localStorage.getItem("very-very-hard") === "true";

export const STARTING_LEVEL = 1 - 1;
export const ENDING_LEVEL = isHardMode ? 10 : 8; // (last playable in terms of UI level)
export const INITIAL_HP = 15;
export const INITIAL_MP = 10;
export const INITIAL_COIN = 0;
export const INITIAL_MAX_COIN = 99;
export const STARTING_COLS = 3;
export const TILE_WIDTH = 128;

export type IStat = number | [number, number];
type IAffinityKey = "fire" | "wind" | "water";
export type IStats = {
  hp: IStat;
  mp: IStat;
  level: number;
  coin?: IStat;
  affinity?: IAffinityKey;
};

export const defaultStats: IStats = {
  hp: [INITIAL_HP, INITIAL_HP],
  mp: [INITIAL_MP, INITIAL_MP],
  level: 0,
  coin: [INITIAL_COIN, INITIAL_MAX_COIN],
};
export const PLAYER_FRAME = "weapons/wand_ice";
export const DEFAULT_FRAME = "items/pie";

export const entityRecord: Record<string, IStats> = {
  // creatures
  // - player
  "weapons/wand_ice": { hp: -4, mp: 3, level: 999 },
  "creatures/monster_humanoid": { hp: 0, mp: 0, level: 999 },
  // - level 1
  "creatures/squirrel": { hp: -1, mp: 0, level: 0 },
  "creatures/bird": { hp: 0, mp: -1, level: 0 },
  "creatures/porcupine": { hp: -3, mp: 0, level: 0 },
  "items/grapes": { hp: 3, mp: 0, level: 0 },
  // - level 2 (MP tutorial)
  "creatures/scorpion": { hp: -4, mp: -4, level: 1 },
  "creatures/mushroom": { hp: -5, mp: -5, level: 1 },
  "creatures/bat": { hp: -3, mp: -3, level: 1 },
  "items/carrot": { hp: -1, mp: 1, level: 1 },
  // - level 3
  "creatures/monster_small_1": { hp: -3, mp: -2, level: 2 },
  "creatures/monster_small_2": { hp: -5, mp: -1, level: 2 },
  "creatures/monster_small_3": { hp: -6, mp: 6, level: 2 },
  "items/salt": { hp: 2, mp: -3, level: 2 },
  // - level 4
  "creatures/monster_small_4": { hp: -4, mp: -1, level: 3 },
  "creatures/monster_small_5": { hp: -5, mp: 0, level: 3 },
  "creatures/monster_small_6": { hp: -7, mp: 1, level: 3 },
  "creatures/monster_small_7": { hp: -8, mp: 2, level: 3 },
  "items/apple": { hp: 1, mp: -1, level: 3 },
  "items/raw_meat": { hp: 2, mp: -2, level: 3 },
  // - level 5 (gold tutorial)
  "creatures/monster_small_8": { hp: -7, mp: 1, coin: 0, level: 4 },
  "creatures/monster_small_9": { hp: -7, mp: 1, level: 1 },
  "creatures/monster_small_10": { hp: -4, mp: -4, level: -4 },
  "creatures/monster_small_11": { hp: -5, mp: -5, coin: -5, level: 4 },
  "creatures/monster_small_12": { hp: -9, mp: -3, coin: -9, level: 4 },
  "creatures/monster_small_13": { hp: -4, mp: -4, level: 4 },
  "items/cooked_meat": { hp: 1, mp: 1, coin: 0, level: 4 },
  "items/flower_5": { hp: 0, mp: 1, coin: 5, level: 4 },
  // - level 6
  "creatures/monster_mid_1": { hp: -4, mp: 0, coin: -4, level: 5 },
  "creatures/monster_mid_2": { hp: -4, mp: 4, coin: 0, level: 5 },
  "creatures/monster_mid_3": { hp: -4, mp: -4, coin: -4, level: 5 },
  "creatures/monster_mid_4": { hp: -6, mp: 6, coin: 0, level: 5 },
  "creatures/monster_mid_5": { hp: -4, mp: -4, coin: 0, level: 5 },
  "items/jar": { hp: 0, mp: -2, coin: -4, level: 5 },
  "items/bag": { hp: 0, mp: 0, coin: -1, level: 5 },
  "items/barrel": { hp: 0, mp: -1, coin: -2, level: 5 },
  // - level 7
  "creatures/monster_mid_6": { hp: -6, mp: -4, coin: -6, level: 6 },
  "creatures/monster_mid_7": { hp: -10, mp: -12, coin: 5, level: 6 },
  "creatures/monster_mid_8": { hp: -11, mp: 3, coin: -9, level: 6 },
  "creatures/monster_mid_9": { hp: 1, mp: -10, coin: -8, level: 6 },
  "creatures/monster_mid_10": { hp: -9, mp: -4, coin: 1, level: 6 },
  "items/bread": { hp: 0, mp: -1, coin: -2, level: 6 },
  "items/bread_1": { hp: -3, mp: -2, coin: -4, level: 6 },
  "items/cheese": { hp: -1, mp: 1, coin: -7, level: 6 },
  "rewards/scroll": { hp: 0, mp: 0, level: 6 },
  "rewards/astrolabe": { hp: 0, mp: 0, level: 6 },
  // - level 8 (reward as item tutorial)
  "creatures/monster_mid_11": { hp: -10, mp: -10, coin: 10, level: 7 },
  "creatures/monster_mid_12": { hp: -9, mp: -9, coin: 0, level: 7 },
  "creatures/monster_mid_13": { hp: -6, mp: -4, coin: 0, level: 7 },
  "creatures/monster_mid_14": { hp: -7, mp: -5, coin: 1, level: 7 },
  "creatures/monster_mid_15": { hp: -7, mp: -7, coin: 0, level: 7 },
  "creatures/monster_mid_16": { hp: -6, mp: -4, coin: 0, level: 7 },
  "creatures/monster_mid_17": { hp: -7, mp: -7, coin: 0, level: 7 },
  "creatures/monster_mid_18": { hp: -6, mp: -4, coin: 1, level: 7 },
  "rewards/meat": { hp: 0, mp: 0, level: 7 },
  "rewards/fish": { hp: 0, mp: 0, level: 7 },
  // - level 9 (as hard as physically possible)
  "creatures/monster_big_1": { hp: -10, mp: 0, level: 8 },
  "creatures/monster_big_2": { hp: -10, mp: 0, level: 8 },
  "creatures/monster_big_3": { hp: -10, mp: 0, level: 8 },
  "creatures/monster_big_4": { hp: 0, mp: -10, level: 8 },
  "creatures/monster_big_5": { hp: 0, mp: -10, level: 8 },
  "rewards/campfire": { hp: 0, mp: 0, level: 8 },
  // - level 10 (throw everything at player)
  "creatures/monster_big_6": { hp: -50, mp: 0, coin: -50, level: 9 },
  "creatures/monster_big_7": { hp: -11, mp: 0, level: 9 },
  "creatures/monster_big_8": { hp: 0, mp: -12, level: 9 },
  "creatures/monster_big_9": { hp: 0, mp: -12, level: 9 },
  "creatures/monster_big_10": { hp: -11, mp: 0, level: 9 },
  "creatures/monster_big_11": { hp: 0, mp: -11, level: 9 },
  "creatures/monster_big_12": { hp: -11, mp: 0, level: 9 },
  "creatures/monster_big_13": { hp: 0, mp: -11, level: 9 },
  "creatures/monster_big_14": { hp: -11, mp: 0, level: 9 },
  "creatures/monster_big_15": { hp: 0, mp: -11, level: 9 },
  "rewards/emerald": { hp: 0, mp: 0, level: 9 },
  "rewards/dream_catcher": { hp: 0, mp: 0, level: 9 },
  // - bonus
  "rewards/diamond": { hp: 10, mp: 10, coin: 10, level: 10 },
  // other
  "creatures/monster_small_23": { hp: -6, mp: -7, level: 999 },
  "creatures/monster_mid_27": { hp: 0, mp: 0, level: 999 },
  "creatures/tree": { hp: -6, mp: 3, level: 999 },
  "creatures/slime": { hp: -4, mp: 3, level: 999 },
  "creatures/monster_furry": { hp: 0, mp: 0, level: 999 },
  "creatures/bug": { hp: -13, mp: 6, level: 999 },
  "creatures/bug_1": { hp: -1, mp: 9, level: 999 },
  "creatures/log_monster": { hp: 0, mp: 0, level: 999 },
  "creatures/fish_deepsea": { hp: -4, mp: 3, level: 999 },
  "creatures/log_walking": { hp: 0, mp: 0, level: 999 },
  "creatures/bird_fat": { hp: 0, mp: 0, level: 999 },
  "creatures/chameleon": { hp: 0, mp: 0, level: 999 },
  "creatures/crab": { hp: 0, mp: 0, level: 999 },
  "creatures/crow": { hp: 0, mp: 0, level: 999 },
  "creatures/crustacean": { hp: 0, mp: 0, level: 999 },
  "creatures/furry": { hp: 0, mp: 0, level: 999 },
  "creatures/fishy": { hp: 0, mp: 0, level: 999 },
  "creatures/fishy_1": { hp: 0, mp: 0, level: 999 },
  "creatures/fishy_2": { hp: 0, mp: 0, level: 999 },
  "creatures/octopus": { hp: 0, mp: 0, level: 999 },
  "creatures/owl": { hp: 0, mp: 0, level: 999 },
  "creatures/root": { hp: 0, mp: 0, level: 999 },
  "creatures/shark": { hp: 0, mp: 0, level: 999 },
  "creatures/snail": { hp: 0, mp: 0, level: 999 },
  "creatures/spider_fat": { hp: 0, mp: 0, level: 999 },
  "creatures/spider_thin": { hp: 0, mp: 0, level: 999 },
  "creatures/tentacles": { hp: 0, mp: 0, level: 999 },
  "creatures/turtle": { hp: 0, mp: 0, level: 999 },
  "creatures/turtle_dumb": { hp: 0, mp: 0, level: 999 },
  // items
  "items/flower_1": { hp: 0, mp: 3, coin: 1, level: 999 },
  "items/flower_2": { hp: 0, mp: 1, coin: 1, level: 999 },
  "items/flower_3": { hp: 1, mp: 1, coin: 1, level: 999 },
  "items/flower_4": { hp: 2, mp: 0, coin: 1, level: 999 },
  "items/bell": { hp: 0, mp: 0, level: 999 },
  "items/candle": { hp: 0, mp: 0, level: 999 },
  "items/chest": { hp: 0, mp: 0, level: 999 },
  "items/fish": { hp: 0, mp: 3, level: 999 },
  "items/key": { hp: 0, mp: 5, level: 999 },
  "items/log": { hp: 0, mp: 1, level: 999 },
  "items/clothes_1": { hp: 0, mp: 0, level: 999 },
  "items/clothes_2": { hp: 0, mp: 0, level: 999 },
  "items/clothes_3": { hp: 0, mp: 0, level: 999 },
  "items/clothes_4": { hp: 0, mp: 0, level: 999 },
  "items/clothes_5": { hp: 0, mp: 0, level: 999 },
  "items/clothes_6": { hp: 0, mp: 0, level: 999 },
  "items/clothes_7": { hp: 0, mp: 0, level: 999 },
  "items/clothes_8": { hp: 0, mp: 0, level: 999 },
  "items/clothes_9": { hp: 0, mp: 0, level: 999 },
  "items/clothes_10": { hp: 0, mp: 0, level: 999 },
  "items/clothes_11": { hp: 0, mp: 0, level: 999 },
  "items/copper": { hp: 0, mp: 0, level: 999 },
  "items/crystals": { hp: 0, mp: 0, level: 999 },
  "items/diamond": { hp: 0, mp: 0, level: 999 },
  "items/emerald": { hp: 0, mp: 0, level: 999 },
  "items/feathers": { hp: 0, mp: 0, level: 999 },
  "items/forge": { hp: 0, mp: 0, level: 999 },
  "items/gold_1": { hp: 0, mp: 0, level: 999 },
  "items/gold_2": { hp: 0, mp: 0, level: 999 },
  "items/gold_3": { hp: 0, mp: 0, level: 999 },
  "items/dagger": { hp: 0, mp: 0, level: 999 },
  "items/loom": { hp: 0, mp: 0, level: 999 },
  "items/map": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_2": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_3": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_4": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_5": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_6": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_7": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_8": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_9": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_1": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_10": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_11": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_12": { hp: 0, mp: 0, level: 999 },
  "items/monster_drop_13": { hp: 0, mp: 0, level: 999 },
  "items/nut": { hp: 0, mp: 0, level: 999 },
  "items/open_book": { hp: 0, mp: 0, level: 999 },
  "items/pear": { hp: 0, mp: 0, level: 999 },
  "items/pepper": { hp: 0, mp: 0, level: 999 },
  "items/perl": { hp: 0, mp: 0, level: 999 },
  "items/pick": { hp: 0, mp: 0, level: 999 },
  "items/pickaxe": { hp: 0, mp: 0, level: 999 },
  "items/pie": { hp: 2, mp: 2, level: 999 },
  "items/plank": { hp: 0, mp: 0, level: 999 },
  "items/potion": { hp: 0, mp: 0, level: 999 },
  "items/potion_1": { hp: 0, mp: 0, level: 999 },
  "items/potion_2": { hp: 0, mp: 0, level: 999 },
  "items/potion_3": { hp: 0, mp: 0, level: 999 },
  "items/potion_4": { hp: 0, mp: 0, level: 999 },
  "items/potion_5": { hp: 0, mp: 0, level: 999 },
  "items/potion_6": { hp: 0, mp: 0, level: 999 },
  "items/potion_7": { hp: 0, mp: 0, level: 999 },
  "items/quilt": { hp: 0, mp: 0, level: 999 },
  "items/ring_1": { hp: 0, mp: 0, level: 999 },
  "items/ring_2": { hp: 0, mp: 0, level: 999 },
  "items/ring_3": { hp: 0, mp: 0, level: 999 },
  "items/ring_4": { hp: 0, mp: 0, level: 999 },
  "items/ring_5": { hp: 0, mp: 0, level: 999 },
  "items/scroll": { hp: 0, mp: 0, level: 999 },
  "items/shield_1": { hp: 0, mp: 0, level: 999 },
  "items/shield_2": { hp: 0, mp: 0, level: 999 },
  "items/shield_3": { hp: 0, mp: 0, level: 999 },
  "items/statue": { hp: 0, mp: 0, level: 999 },
  "items/sword": { hp: 0, mp: 0, level: 999 },
  "items/torch": { hp: 0, mp: 0, level: 999 },
  "items/wand_1": { hp: 0, mp: 0, level: 999 },
  "items/wand_2": { hp: 0, mp: 0, level: 999 },
  "items/wand_3": { hp: 0, mp: 0, level: 999 },
  "items/weapon_1": { hp: 0, mp: 0, level: 999 },
  "items/weapon_2": { hp: 0, mp: 0, level: 999 },
  "items/weapon_3": { hp: 0, mp: 0, level: 999 },
  "items/weapon_4": { hp: 0, mp: 0, level: 999 },
  "items/weapon_5": { hp: 0, mp: 0, level: 999 },
  "items/weapon_6": { hp: 0, mp: 0, level: 999 },
  "items/weapon_7": { hp: 0, mp: 0, level: 999 },
  "items/weapon_8": { hp: 0, mp: 0, level: 999 },
  "items/weapon_9": { hp: 0, mp: 0, level: 999 },
  "items/weapon_10": { hp: 0, mp: 0, level: 999 },
  "items/weapon_11": { hp: 0, mp: 0, level: 999 },
  "items/weapon_12": { hp: 0, mp: 0, level: 999 },
  "items/weapon_13": { hp: 0, mp: 0, level: 999 },
  "items/weapon_14": { hp: 0, mp: 0, level: 999 },
  "items/weapon_15": { hp: 0, mp: 0, level: 999 },
  "items/weapon_16": { hp: 0, mp: 0, level: 999 },
  "items/yarn": { hp: 0, mp: 0, level: 999 },

  // rewards
  "rewards/nautilus": { hp: 0, mp: 0, level: 999 },
  "rewards/red_amulet": { hp: 0, mp: 0, level: 999 },
  "rewards/blue_amulet": { hp: 0, mp: 0, level: 999 },
  "rewards/crystal_ball": { hp: 0, mp: 0, level: 999 },
  // value only (collectable)
  "rewards/nut": { hp: 0, mp: 0, level: 999 },
  "rewards/pinecone": { hp: 0, mp: 0, level: 999 },
  // level 6+
  "rewards/gold_pot": { hp: 0, mp: 0, level: 999 },
};

export const levelRewards = [
  "rewards/campfire",
  "rewards/meat",
  "rewards/fish",
  "rewards/nautilus",
  "rewards/red_amulet",
  "rewards/blue_amulet",
  "rewards/crystal_ball",
  "rewards/scroll",
  "rewards/astrolabe",
  // value only (collectable)
  "rewards/nut",
  "rewards/pinecone",
  "rewards/diamond",
  "rewards/emerald",
  // level 6+
  "rewards/gold_pot",
  "rewards/dream_catcher",
];
